"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form"

import { RequiredMarker } from "@/components/required-fields-legend"
import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"
import { inputFieldVariants, labelFieldVariants } from "./field-variants"

const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState } = useFormContext()

  if (fieldContext.name === undefined) {
    throw new Error("useFormField should be used within <FormField>")
  }

  const { id } = itemContext
  if (!id) {
    throw new Error("useFormField should be used within <FormItem>")
  }

  const formState = useFormState({ name: fieldContext.name })
  const fieldState = getFieldState(fieldContext.name, formState)

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    formHelpId: `${id}-form-item-help`,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
  variant: "default" | "floating"
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue
)

type FormItemProps = React.ComponentProps<"div"> & {
  /** Use `floating` for text fields with a label that animates above the input (CRM-style). */
  variant?: "default" | "floating"
}

/**
 * Wraps one control. For floating labels, place the control first, then {@link FormLabel} with `variant="floating"`, then {@link FormMessage}.
 */
function FormItem({ className, variant = "floating", ...props }: FormItemProps) {
  const id = React.useId()

  return (
    <FormItemContext.Provider value={{ id, variant }}>
      <div
        data-slot="form-item"
        data-variant={variant}
        className={cn(
          "grid",
          variant === "floating" ? "relative gap-0" : "gap-2",
          className
        )}
        {...props}
      />
    </FormItemContext.Provider>
  )
}

type FormLabelProps = Omit<React.ComponentProps<typeof Label>, "htmlFor"> & {
  /** When set, appends a non-breaking visual asterisk (use instead of typing `*` in children). */
  required?: boolean
}

function FormLabel({
  className,
  variant: variantProp,
  required,
  children,
  ...props
}: FormLabelProps) {
  const { error, formItemId } = useFormField()
  const itemContext = React.useContext(FormItemContext)
  const variant = variantProp ?? itemContext.variant ?? "floating"

  return (
    <Label
      data-slot="form-label"
      variant={variant}
      data-error={!!error}
      className={cn("data-[error=true]:text-destructive", labelFieldVariants({ variant }), className)}
      {...props}
      htmlFor={formItemId}
    >
      {children}
      {required ? <RequiredMarker /> : null}
    </Label>
  )
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()
  const itemContext = React.useContext(FormItemContext)
  const controlVariant = itemContext.variant ?? "floating"

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      className={cn(inputFieldVariants({ variant: controlVariant }))}

      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
  const { formDescriptionId } = useFormField()

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message ?? "") : props.children

  if (!body) {
    return null
  }

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      role={error ? "alert" : undefined}
      className={cn("mt-1 px-4 text-sm text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  )
}

function FormHelpText({ className, ...props }: React.ComponentProps<"p">) {
  const { formHelpId } = useFormField()

  return (
    <p
      data-slot="form-help-text"
      id={formHelpId}
      className={cn("mt-1 px-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormHelpText,
  FormField,
}
